import { ChallengeCurrency } from '../enums/ChallengeCurrency';

export const MIN_CHALLENGE_PRICE = 0.01;
export const MAX_CHALLENGE_PRICE = 2_000;

export const SUPPORTED_CHALLENGE_CURRENCIES: readonly ChallengeCurrency[] = Object.freeze([
  ChallengeCurrency.EUR,
  ChallengeCurrency.USD,
]);

export function getSupportedChallengeCurrencies(): ChallengeCurrency[] {
  return [...SUPPORTED_CHALLENGE_CURRENCIES];
}

export function isSupportedChallengeCurrency(value: unknown): value is ChallengeCurrency {
  return typeof value === 'string' && SUPPORTED_CHALLENGE_CURRENCIES.includes(value as ChallengeCurrency);
}

export function isValidChallengePrice(value: unknown): value is number {
  if (
    typeof value !== 'number' ||
    !Number.isFinite(value) ||
    value < MIN_CHALLENGE_PRICE ||
    value > MAX_CHALLENGE_PRICE
  ) {
    return false;
  }

  const roundedToCents = Math.round(value * 100) / 100;
  return Math.abs(value - roundedToCents) < 1e-9;
}

export function isValidChallengePriceConfiguration(
  amount: unknown,
  currency: unknown,
): currency is ChallengeCurrency {
  return isValidChallengePrice(amount) && isSupportedChallengeCurrency(currency);
}

export function formatChallengePrice(
  amount: unknown,
  currency: unknown,
  locale = 'en-US',
): string | null {
  if (!isValidChallengePrice(amount) || !isSupportedChallengeCurrency(currency)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}
